import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  Button,
  CalendarDate,
  CalendarCheck01,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  EmptyState,
  LayoutGrid01,
  Menu,
  PageHeader,
  ProgressBar,
  Select,
  Stars01,
  Tabs,
  UserCheck01,
} from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastInfo, toastSuccess } from "../../shared/toastHelper";
import { StaffingScheduleContext } from "./components/StaffingScheduleContext";
import StaffingTabs from "./components/StaffingTabs";
import ScheduleGrid from "./components/ScheduleGrid";
import DayTimeline from "./components/DayTimeline";
import ShiftDrawer from "./components/ShiftDrawer";
import PublishReviewModal from "./components/PublishReviewModal";
import {
  addDays,
  hoursLabel,
  percentLabel,
  weekLabel,
  wholeDollars,
} from "./components/scheduleFormatters";
import StaffWeekModal from "./components/StaffWeekModal";
import SaveWeekTemplateModal from "./components/SaveWeekTemplateModal";
import SmartScheduleModal from "./components/SmartScheduleModal";

const ALL_ROOMS = "all";
const VIEWS = [
  { value: "staff", label: "By staff" },
  { value: "position", label: "By position" },
  { value: "day", label: "Day" },
];
const NARROW = "(max-width: 639px)";

const viewFromUrl = () => {
  const view = new URLSearchParams(window.location.search).get("view");
  return VIEWS.some((item) => item.value === view) ? view : VIEWS[0].value;
};

const useNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => window.matchMedia?.(NARROW).matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.(NARROW);
    if (!query) return undefined;
    const update = () => setNarrow(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return narrow;
};

const firstShowDay = (schedule) =>
  schedule.columns[0]?.date ?? schedule.week.starts_on;

const StatCard = ({ label, children }) => (
  <div
    style={{
      background: "var(--sl-bg-primary)",
      border: "1px solid var(--sl-border-secondary)",
      borderRadius: "var(--sl-radius-xl)",
      padding: "12px 16px",
      boxShadow: "var(--sl-shadow-xs)",
    }}
  >
    <div
      style={{
        fontSize: 12,
        lineHeight: "18px",
        color: "var(--sl-text-tertiary)",
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const StatValue = ({ children, color }) => (
  <div
    style={{
      fontSize: 20,
      lineHeight: "30px",
      fontWeight: 600,
      color: color || "var(--sl-text-primary)",
    }}
  >
    {children}
  </div>
);

StatValue.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
};

const LaborBudgetCard = ({ stats }) => {
  const budgeted = stats.weekly_budget != null;
  const percent = stats.budget_pct;
  const over = percent != null && percent > 100;
  let percentText = null;
  if (budgeted) {
    percentText = over
      ? `${percentLabel(percent)} · over budget`
      : percentLabel(percent);
  }

  return (
    <div
      style={{
        background: "var(--sl-bg-primary)",
        border: "1px solid var(--sl-border-secondary)",
        borderRadius: "var(--sl-radius-xl)",
        padding: "12px 16px",
        boxShadow: "var(--sl-shadow-xs)",
        gridColumn: "span 2",
        minWidth: 0,
      }}
    >
      <div
        className="d-flex justify-content-between"
        style={{
          gap: 8,
          fontSize: 12,
          lineHeight: "18px",
          color: "var(--sl-text-tertiary)",
        }}
      >
        <span>Scheduled labor</span>
        {percentText && (
          <span
            style={{
              fontWeight: 600,
              color: over ? "var(--sl-text-warning)" : undefined,
            }}
          >
            {percentText}
          </span>
        )}
      </div>
      <div className="d-flex align-items-baseline" style={{ gap: 8 }}>
        <StatValue>{wholeDollars(stats.scheduled_cost)}</StatValue>
        <span style={{ fontSize: 12, color: "var(--sl-text-quaternary)" }}>
          of {budgeted ? wholeDollars(stats.weekly_budget) : "—"} weekly budget
        </span>
      </div>
      <ProgressBar
        style={{ marginTop: 6 }}
        value={percent == null ? 0 : Math.min(100, percent)}
        tone={over ? "warning" : "brand"}
        aria-label="Scheduled labor against the weekly budget"
      />
    </div>
  );
};

LaborBudgetCard.propTypes = {
  stats: PropTypes.shape({
    scheduled_cost: PropTypes.string.isRequired,
    weekly_budget: PropTypes.string,
    budget_pct: PropTypes.number,
  }).isRequired,
};

const WeekActions = ({
  onStaffWeek,
  onSmartSchedule,
  onApplyWeekTemplate,
  onSaveWeek,
}) => (
  <>
    {onSaveWeek ? (
      <Menu
        placement="bottom-end"
        items={[
          {
            key: "apply",
            label: "Apply a week template…",
            onClick: onApplyWeekTemplate,
            disabled: !onApplyWeekTemplate,
          },
          {
            key: "save",
            label: "Save this week as a template…",
            onClick: onSaveWeek,
          },
        ]}
        trigger={
          <Button
            variant="secondary"
            icon={LayoutGrid01}
            trailingIcon={ChevronDown}
          >
            Week templates
          </Button>
        }
      />
    ) : (
      <Button variant="secondary" icon={LayoutGrid01} disabled>
        Week templates
      </Button>
    )}
    <Button
      variant="secondary"
      icon={Stars01}
      disabled={!onSmartSchedule}
      onClick={onSmartSchedule}
    >
      Smart Schedule
    </Button>
    <Button
      variant="secondary"
      icon={CalendarCheck01}
      disabled={!onStaffWeek}
      onClick={onStaffWeek}
    >
      Staff this week
    </Button>
  </>
);

WeekActions.propTypes = {
  onStaffWeek: PropTypes.func,
  onSmartSchedule: PropTypes.func,
  onApplyWeekTemplate: PropTypes.func,
  onSaveWeek: PropTypes.func,
};

const StaffingScheduleApp = (props) => {
  const { tabPaths, venues, paths, permissions } = props;
  const [schedule, setSchedule] = useState(props.schedule);
  const [venueOwnershipId, setVenueOwnershipId] = useState(
    props.venueOwnershipId,
  );
  const [room, setRoom] = useState(ALL_ROOMS);
  const [view, setView] = useState(viewFromUrl);
  const [day, setDay] = useState(
    () => props.schedule && firstShowDay(props.schedule),
  );
  const narrow = useNarrow();
  const [staffWeekPreview, setStaffWeekPreview] = useState(null);
  const [smartSchedulePreview, setSmartSchedulePreview] = useState(null);
  const [weekTemplateOptions, setWeekTemplateOptions] = useState(
    props.weekTemplates?.options || [],
  );
  const [weekTemplatePreview, setWeekTemplatePreview] = useState(null);
  const [savingWeek, setSavingWeek] = useState(false);
  const [editing, setEditing] = useState(null);
  const [publishPreview, setPublishPreview] = useState(null);
  const [cancellingEventId, setCancellingEventId] = useState(null);
  const [changingTimesEventId, setChangingTimesEventId] = useState(null);

  const latestLoad = useRef(0);
  const requestedVenueOwnershipId = useRef(props.venueOwnershipId);
  const requestedWeek = useRef(props.schedule?.week.starts_on);
  const displayedVenueOwnershipId = useRef(props.venueOwnershipId);
  const displayedWeek = useRef(props.schedule?.week.starts_on);

  const load = async (nextVenueOwnershipId, week) => {
    const loadId = ++latestLoad.current;
    requestedVenueOwnershipId.current = nextVenueOwnershipId;
    requestedWeek.current = week;
    try {
      const response = await client.get(paths.schedule, {
        params: { venue_ownership_id: nextVenueOwnershipId, week },
      });
      if (loadId !== latestLoad.current) return;
      displayedVenueOwnershipId.current = nextVenueOwnershipId;
      displayedWeek.current = response.data.week.starts_on;
      setSchedule(response.data);
      setDay((current) =>
        current >= response.data.week.starts_on &&
        current <= response.data.week.ends_on
          ? current
          : firstShowDay(response.data),
      );
      setVenueOwnershipId(nextVenueOwnershipId);
    } catch (error) {
      if (loadId !== latestLoad.current) return;
      requestedVenueOwnershipId.current = displayedVenueOwnershipId.current;
      requestedWeek.current = displayedWeek.current;
      toastError("Could not load this week.");
    }
  };

  const changeView = (nextView) => {
    setView(nextView);
    const url = new URL(window.location.href);
    url.searchParams.set("view", nextView);
    window.history.replaceState(window.history.state, "", url);
  };

  const reload = () =>
    load(requestedVenueOwnershipId.current, requestedWeek.current);

  const cancelShifts = async (column, shifts) => {
    setCancellingEventId(column.id);
    try {
      for (const shift of shifts) {
        await client.delete(`${paths.shifts}/${shift.id}`);
      }
      const next = shifts.every((shift) => shift.state === "draft")
        ? " They were drafts, so nobody was notified."
        : " Publish to tell the staff on them.";
      toastSuccess(
        `Cancelled ${shifts.length} ${shifts.length === 1 ? "shift" : "shifts"} for ${column.title}.${next}`,
      );
    } catch (error) {
      toastError("Could not cancel every shift.");
    }
    await reload();
    setCancellingEventId(null);
  };

  const changeShowTimes = async (column, decision) => {
    setChangingTimesEventId(column.id);
    try {
      await client.post(paths.showTimeChanges, {
        calendar_event_id: column.id,
        decision,
      });
      const count = column.moved.shift_count;
      toastSuccess(
        decision === "match"
          ? `Moved ${count} ${count === 1 ? "shift" : "shifts"} for ${column.title}. Publish to tell the staff on them.`
          : `Kept the shift times for ${column.title}.`,
      );
    } catch (error) {
      toastError(
        error.response?.data?.errors?.join(" ") ||
          "Could not change the shift times.",
      );
    }
    await reload();
    setChangingTimesEventId(null);
  };

  const openPublish = async () => {
    if (schedule.stats.publishable_count === 0) {
      toastInfo(
        schedule.stats.unpublished_count > 0
          ? "Nothing you can publish this week."
          : "Nothing to publish. Every shift this week is live.",
      );
      return;
    }
    const scope = {
      venue_ownership_id: venueOwnershipId,
      week: schedule.week.starts_on,
    };
    try {
      const response = await client.get(paths.publicationPreview, {
        params: scope,
      });
      if (
        requestedVenueOwnershipId.current !== scope.venue_ownership_id ||
        requestedWeek.current !== scope.week
      )
        return;
      setPublishPreview({ ...response.data, scope });
    } catch (error) {
      toastError("Could not load what will be published.");
    }
  };

  const openStaffWeek = async () => {
    const params = {
      venue_ownership_id: requestedVenueOwnershipId.current,
      week: requestedWeek.current,
    };
    try {
      const response = await client.get(props.staffWeek.preview, { params });
      setStaffWeekPreview({ ...response.data, params });
    } catch (error) {
      toastError("Could not load this week's shows.");
    }
  };
  const onStaffWeek =
    permissions.manageSchedule && props.staffWeek ? openStaffWeek : undefined;

  const openSmartSchedule = async () => {
    const params = {
      venue_ownership_id: requestedVenueOwnershipId.current,
      week: requestedWeek.current,
    };
    try {
      const response = await client.get(props.smartSchedule.preview, {
        params,
      });
      if (
        requestedVenueOwnershipId.current !== params.venue_ownership_id ||
        requestedWeek.current !== params.week
      )
        return;
      setSmartSchedulePreview({ ...response.data, params });
    } catch (error) {
      toastError("Could not build a Smart Schedule for this week.");
    }
  };
  const onSmartSchedule =
    permissions.manageSchedule && props.smartSchedule
      ? openSmartSchedule
      : undefined;

  const venueWeekTemplates = weekTemplateOptions.filter(
    (option) => option.venue_ownership_id === venueOwnershipId,
  );

  const openWeekTemplate = async (templateId) => {
    const params = {
      shift_template_id: templateId,
      week: requestedWeek.current,
    };
    try {
      const response = await client.get(props.weekTemplates.preview, {
        params,
      });
      if (requestedWeek.current !== params.week) return;
      setWeekTemplatePreview({ ...response.data, params });
    } catch (error) {
      toastError("Could not load what this template will create.");
    }
  };
  const canUseWeekTemplates = permissions.manageSchedule && props.weekTemplates;
  const onApplyWeekTemplate =
    canUseWeekTemplates && venueWeekTemplates.length > 0
      ? () => openWeekTemplate(venueWeekTemplates[0].value)
      : undefined;
  const onSaveWeek = canUseWeekTemplates
    ? () => setSavingWeek(true)
    : undefined;

  useEffect(() => {
    if (props.staffWeek?.open && onStaffWeek && props.schedule) openStaffWeek();
  }, []);

  const roomItems = useMemo(() => {
    if (!schedule) return [];
    const rooms = new Map();
    schedule.columns.forEach((column) =>
      column.room_ids.forEach((id, index) =>
        rooms.set(id, column.room_names[index]),
      ),
    );
    return [
      { value: ALL_ROOMS, label: "All rooms" },
      ...Array.from(rooms, ([value, label]) => ({ value, label })),
    ];
  }, [schedule]);

  const activeRoom = roomItems.some((item) => item.value === room)
    ? room
    : ALL_ROOMS;
  const visibleColumns = schedule
    ? schedule.columns.filter(
        (column) =>
          activeRoom === ALL_ROOMS ||
          column.day ||
          column.room_ids.includes(activeRoom),
      )
    : [];
  const confirmedCount = schedule
    ? schedule.columns.filter((column) => column.status === "confirm").length
    : 0;

  const header = (
    <>
      <PageHeader
        title="Staffing"
        supportingText="Build each week's shifts around the shows on your calendar."
      />
      <div className="mt-3">
        <StaffingTabs current="schedule" paths={tabPaths} />
      </div>
    </>
  );

  if (!schedule) {
    return (
      <div className="od-themed" style={{ padding: 24 }}>
        {header}
        <EmptyState
          icon={CalendarDate}
          title="No venues to schedule"
          description="You need access to a venue before you can build its schedule."
        />
      </div>
    );
  }

  const context = { ...props, schedule, venueOwnershipId };
  const addShift = (row, column, person) =>
    setEditing({
      staff_position_id: row.id,
      staff_member_id: person?.id,
      calendar_event_id: column.day ? null : column.id,
      work_date: column.date,
      start_clock: column.doors_clock || column.show_clock || "17:00",
      end_clock: column.end_clock || "23:00",
      unpaid_break_minutes: 0,
      notes: "",
    });

  return (
    <StaffingScheduleContext.Provider value={context}>
      <div className="od-themed" style={{ padding: 24 }}>
        {header}

        <div
          className="d-flex flex-wrap align-items-center justify-content-between mb-3"
          style={{ gap: 12 }}
        >
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: 12 }}
          >
            <Select
              id="schedule-venue"
              width={220}
              options={venues}
              value={venueOwnershipId}
              onChange={(value) => value && load(value, requestedWeek.current)}
            />
            <div
              className="d-flex align-items-center"
              style={{
                border: "1px solid var(--sl-border-primary)",
                borderRadius: "var(--sl-radius-md)",
                background: "var(--sl-bg-primary)",
              }}
            >
              <Button
                variant="tertiary"
                icon={ChevronLeft}
                aria-label="Previous week"
                onClick={() =>
                  load(
                    requestedVenueOwnershipId.current,
                    addDays(requestedWeek.current, -7),
                  )
                }
              />
              <span
                style={{
                  padding: "0 12px",
                  fontSize: 15,
                  fontWeight: 500,
                  color: "var(--sl-text-primary)",
                  whiteSpace: "nowrap",
                }}
              >
                {weekLabel(schedule.week.starts_on, schedule.week.ends_on)}
              </span>
              <Button
                variant="tertiary"
                icon={ChevronRight}
                aria-label="Next week"
                onClick={() =>
                  load(
                    requestedVenueOwnershipId.current,
                    addDays(requestedWeek.current, 7),
                  )
                }
              />
            </div>
            <Tabs
              variant="button-border"
              items={VIEWS}
              value={view}
              onChange={changeView}
              fullWidth={narrow}
              collapseBelow={0}
            />
            {roomItems.length > 2 && (
              <Tabs
                variant="button-border"
                items={roomItems}
                value={activeRoom}
                onChange={setRoom}
                collapseBelow={0}
              />
            )}
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
            <WeekActions
              onStaffWeek={onStaffWeek}
              onSmartSchedule={onSmartSchedule}
              onApplyWeekTemplate={onApplyWeekTemplate}
              onSaveWeek={onSaveWeek}
            />
            <Button
              variant="primary"
              disabled={!schedule.canPublish || cancellingEventId != null}
              onClick={openPublish}
              style={
                schedule.stats.publishable_count === 0
                  ? { opacity: 0.5 }
                  : undefined
              }
            >
              Publish{" "}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 12,
                  lineHeight: "18px",
                  padding: "0 6px",
                  borderRadius: 999,
                  background: "var(--sl-bg-brand-solid-hover)",
                }}
              >
                {schedule.stats.publishable_count}
              </span>
            </Button>
          </div>
        </div>

        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          <StatCard label="Scheduled hours">
            <StatValue>
              {hoursLabel(schedule.stats.scheduled_minutes)}
            </StatValue>
          </StatCard>
          {permissions.readWages && schedule.stats.scheduled_cost != null && (
            <LaborBudgetCard stats={schedule.stats} />
          )}
          <StatCard label="Open shifts">
            <StatValue
              color={
                schedule.stats.open_count > 0
                  ? "var(--sl-text-warning)"
                  : undefined
              }
            >
              {schedule.stats.open_count}
            </StatValue>
          </StatCard>
          <StatCard label="Warnings">
            <StatValue>{schedule.stats.warning_count}</StatValue>
          </StatCard>
          <StatCard label="Unpublished changes">
            <StatValue>{schedule.stats.unpublished_count}</StatValue>
          </StatCard>
        </div>

        {schedule.shifts.length === 0 && !props.hasStaff && (
          <div className="mb-3">
            <EmptyState
              icon={UserCheck01}
              title="Start with your people"
              description="Add the people who work your events, paid staff and volunteers alike, and set up the departments and positions they work. Shift schedules will build on them."
              primaryAction={{
                label: props.canManageRoster
                  ? "Add your people"
                  : "View people",
                onClick: () => {
                  window.location.href = tabPaths.people;
                },
              }}
              secondaryAction={
                tabPaths.positions && {
                  label: props.canManageRoster
                    ? "Set up departments & positions"
                    : "View departments & positions",
                  onClick: () => {
                    window.location.href = tabPaths.positions;
                  },
                }
              }
            />
          </div>
        )}

        {schedule.shifts.length === 0 && props.hasStaff && (
          <div className="mb-3">
            <EmptyState
              icon={CalendarDate}
              title={`No shifts for ${weekLabel(schedule.week.starts_on, schedule.week.ends_on)}`}
              description={`${confirmedCount} confirmed ${confirmedCount === 1 ? "show is" : "shows are"} on the calendar. Staff this week creates open shifts from each room's event template; then fill them or let staff claim them.`}
              secondaryAction={{
                label: "Smart Schedule",
                onClick: onSmartSchedule,
                disabled: !onSmartSchedule,
              }}
              primaryAction={{
                label: "Staff this week",
                onClick: onStaffWeek,
                disabled: !onStaffWeek,
              }}
            />
          </div>
        )}

        {view === "day" ? (
          <DayTimeline
            columns={visibleColumns}
            day={day}
            onDayChange={setDay}
            onOpenShift={setEditing}
            onAddShift={addShift}
            narrow={narrow}
            cancellingEventId={cancellingEventId}
            onCancelShifts={cancelShifts}
            changingTimesEventId={changingTimesEventId}
            onChangeShowTimes={changeShowTimes}
          />
        ) : (
          <ScheduleGrid
            view={view}
            narrow={narrow}
            columns={visibleColumns}
            room={activeRoom === ALL_ROOMS ? undefined : activeRoom}
            onOpenShift={setEditing}
            cancellingEventId={cancellingEventId}
            onCancelShifts={cancelShifts}
            changingTimesEventId={changingTimesEventId}
            onChangeShowTimes={changeShowTimes}
            onAddShift={addShift}
          />
        )}

        <div
          className="d-flex flex-wrap align-items-center mt-3"
          style={{ gap: 16, fontSize: 13, color: "var(--sl-text-tertiary)" }}
        >
          <span className="d-flex align-items-center" style={{ gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 10,
                borderRadius: 3,
                border: "1px solid var(--sl-border-secondary)",
                background: "var(--sl-bg-primary)",
              }}
            />
            Published
          </span>
          <span className="d-flex align-items-center" style={{ gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 10,
                borderRadius: 3,
                border: "1px dashed var(--sl-border-primary)",
                background: "var(--sl-bg-secondary)",
              }}
            />
            Draft (staff can&apos;t see)
          </span>
          <span className="d-flex align-items-center" style={{ gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 10,
                borderRadius: 3,
                border: "1px dashed var(--sl-border-warning)",
                background: "var(--sl-bg-warning-primary)",
              }}
            />
            Open shift
          </span>
          {view === "staff" && <span>Weekly hours count every venue</span>}
          <span>All times {schedule.timeZoneLabel}</span>
        </div>

        {publishPreview && (
          <PublishReviewModal
            preview={publishPreview}
            onClose={() => setPublishPreview(null)}
            onPublished={() => {
              setPublishPreview(null);
              reload();
            }}
          />
        )}

        {editing && (
          <ShiftDrawer
            key={
              editing.id ? `${editing.id}:${editing.warnings.join("|")}` : "new"
            }
            shift={editing}
            onClose={() => setEditing(null)}
            onSaved={(saved) => {
              reload();
              setEditing(saved?.warnings?.length ? saved : null);
            }}
          />
        )}

        {staffWeekPreview && (
          <StaffWeekModal
            preview={staffWeekPreview}
            applyPath={props.staffWeek.apply}
            venueOwnershipId={staffWeekPreview.params.venue_ownership_id}
            week={staffWeekPreview.params.week}
            onClose={() => setStaffWeekPreview(null)}
            onApplied={() => {
              setStaffWeekPreview(null);
              reload();
            }}
          />
        )}

        {weekTemplatePreview && (
          <StaffWeekModal
            preview={weekTemplatePreview}
            applyPath={props.weekTemplates.apply}
            applyParams={weekTemplatePreview.params}
            title="Apply a week template"
            description={`Creates draft open shifts for ${weekLabel(schedule.week.starts_on, schedule.week.ends_on)}. Shifts this template already made this week are skipped, and event shifts from Staff this week aren't touched. Nobody is notified until you publish.`}
            onClose={() => setWeekTemplatePreview(null)}
            onApplied={() => {
              setWeekTemplatePreview(null);
              reload();
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <Select
                id="apply-week-template"
                label="Template"
                options={venueWeekTemplates}
                value={weekTemplatePreview.params.shift_template_id}
                onChange={(value) => value && openWeekTemplate(value)}
              />
            </div>
          </StaffWeekModal>
        )}

        {savingWeek && (
          <SaveWeekTemplateModal
            schedule={schedule}
            savePath={props.weekTemplates.save}
            venueOwnershipId={venueOwnershipId}
            defaultName={`Week of ${weekLabel(schedule.week.starts_on, schedule.week.ends_on)}`}
            onClose={() => setSavingWeek(false)}
            onSaved={(option) => {
              setSavingWeek(false);
              setWeekTemplateOptions((current) => [...current, option]);
            }}
          />
        )}

        {smartSchedulePreview && (
          <SmartScheduleModal
            preview={smartSchedulePreview}
            applyPath={props.smartSchedule.apply}
            venueOwnershipId={smartSchedulePreview.params.venue_ownership_id}
            week={smartSchedulePreview.params.week}
            onClose={() => setSmartSchedulePreview(null)}
            onApplied={() => {
              setSmartSchedulePreview(null);
              reload();
            }}
          />
        )}
      </div>
      <ToastContainer icon={false} />
    </StaffingScheduleContext.Provider>
  );
};

StaffingScheduleApp.propTypes = {
  tabPaths: PropTypes.object.isRequired,
  hasStaff: PropTypes.bool.isRequired,
  canManageRoster: PropTypes.bool.isRequired,
  venues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  venueOwnershipId: PropTypes.number,
  staffWeek: PropTypes.shape({
    preview: PropTypes.string.isRequired,
    apply: PropTypes.string.isRequired,
    open: PropTypes.bool,
  }),
  weekTemplates: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        venue_ownership_id: PropTypes.number.isRequired,
      }),
    ).isRequired,
    preview: PropTypes.string.isRequired,
    apply: PropTypes.string.isRequired,
    save: PropTypes.string.isRequired,
  }),
  smartSchedule: PropTypes.shape({
    preview: PropTypes.string.isRequired,
    apply: PropTypes.string.isRequired,
  }),
  schedule: PropTypes.shape({
    week: PropTypes.shape({
      starts_on: PropTypes.string.isRequired,
      ends_on: PropTypes.string.isRequired,
    }).isRequired,
    timeZoneLabel: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    shifts: PropTypes.arrayOf(PropTypes.object).isRequired,
    staffOptions: PropTypes.object.isRequired,
    weeklyOvertimeAfterMinutes: PropTypes.number,
    stats: PropTypes.object.isRequired,
    canPublish: PropTypes.bool,
  }),
  permissions: PropTypes.shape({
    manageSchedule: PropTypes.bool.isRequired,
    readWages: PropTypes.bool.isRequired,
  }).isRequired,
  paths: PropTypes.shape({
    schedule: PropTypes.string.isRequired,
    shifts: PropTypes.string.isRequired,
    showTimeChanges: PropTypes.string.isRequired,
    publications: PropTypes.string.isRequired,
    publicationPreview: PropTypes.string.isRequired,
  }).isRequired,
};

export default StaffingScheduleApp;
